import React, { useState, useMemo } from 'react';
import { Check, Shield, Database, Sparkles, MessageSquare, Globe, Smartphone, Send, ArrowRight, Info, X, Loader2, Tag, Palette, Server, Brain, Cloud, ChevronDown } from 'lucide-react';
import { db, auth } from '../services/firebase.ts';
import { collection, addDoc } from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';

interface PricingItem {
  id: string;
  name: string;
  description: string;
  oneTime: number;
  monthly: number;
  icon: React.ElementType;
  required?: boolean;
}

const Pricing: React.FC = () => {
  const [aiRoute, setAiRoute] = useState<'ours' | 'own'>('ours');
  const [brandRoute, setBrandRoute] = useState<'own' | 'pro'>('own');
  const [hostingRoute, setHostingRoute] = useState<'ours' | 'own'>('ours');
  const [domainRoute, setDomainRoute] = useState<'own' | 'setup'>('own');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', business: '' });
  const [promoCode, setPromoCode] = useState('');
  
  // Mobile Summary Toggle
  const [isMobileSummaryOpen, setIsMobileSummaryOpen] = useState(false);
  
  const [selected, setSelected] = useState<Record<string, boolean>>({
    base: true,
    tracking: false,
    chatbot: false,
    whatsapp: false,
    telegram: false,
  });

  const isDiscounted = promoCode.trim().toUpperCase() === 'FRIENDSOFTENGAH';

  const toggleItem = (id: string) => {
    if (id === 'base') return;
    setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const totals = useMemo(() => {
    let oneTime = isDiscounted ? 199 : 599;
    let monthly = 0;

    if (selected.chatbot) oneTime += 249;
    if (selected.tracking) oneTime += 249;
    if (selected.whatsapp) oneTime += 69;
    if (selected.telegram) oneTime += 69;
    
    if (domainRoute === 'setup') oneTime += 39; 
    if (brandRoute === 'pro') oneTime += 99;

    if (aiRoute === 'ours') {
      if (selected.chatbot) monthly += 20;
      if (selected.tracking) monthly += 10;
    }

    if (hostingRoute === 'ours') {
      monthly += 14;
    }

    return { oneTime, monthly };
  }, [selected, aiRoute, brandRoute, hostingRoute, domainRoute, isDiscounted]);

  const ensureUser = async () => {
    if (!auth) throw new Error("Firebase Auth Offline");
    if (!auth.currentUser) await signInAnonymously(auth);
  };

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!db || !auth) {
      alert("System is currently offline (Demo Mode). Please contact via WhatsApp.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await ensureUser();

      const newLead = {
        ...formData,
        selections: Object.entries(selected).filter(([_, v]) => v).map(([k]) => k),
        oneTime: totals.oneTime,
        monthly: totals.monthly,
        aiRoute,
        brandRoute,
        hostingRoute,
        domainRoute,
        promoUsed: isDiscounted ? 'FRIENDSOFTENGAH' : 'NONE',
        date: new Date().toISOString()
      };

      // Following Section 5.1: apps/{appId}/records
      await addDoc(collection(db, 'apps/tiny-systems/system_leads'), newLead);
      
      setIsSuccess(true);
      setFormData({ name: '', email: '', business: '' });
    } catch (err) {
      console.error("Firestore Save Error:", err);
      alert("Something went wrong saving your spec. Please contact us via WhatsApp!");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setIsSuccess(false), 300);
  };

  const ItemCard = ({ id, name, description, oneTime, monthly, icon: Icon, required }: PricingItem) => {
    const isSelected = selected[id];
    const displayMonthly = (id === 'chatbot' || id === 'tracking') && aiRoute === 'ours' ? monthly : 0;

    return (
      <div 
        onClick={() => toggleItem(id)}
        className={`group relative p-6 md:p-8 rounded-[2rem] md:rounded-[2.5rem] border-2 cursor-pointer transition-all duration-300 ${
          isSelected 
            ? 'border-[#7FAF9A] bg-white shadow-xl shadow-green-900/5' 
            : 'border-[#1F2933]/5 bg-white/40 grayscale hover:grayscale-0 hover:border-[#7FAF9A]/30'
        }`}
      >
        <div className="flex items-start gap-4 md:gap-5">
          <div className={`p-3 md:p-4 rounded-2xl transition-colors shrink-0 ${isSelected ? 'bg-[#7FAF9A] text-white shadow-lg shadow-green-900/10' : 'bg-[#1F2933]/5 text-[#1F2933]/30'}`}>
            <Icon className="w-6 h-6 md:w-7 md:h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center justify-between mb-2 gap-2">
              <h4 className="text-lg md:text-xl font-black text-[#1F2933] break-words leading-tight">{name}</h4>
              {required && <span className="text-[9px] md:text-[10px] bg-[#7FAF9A]/10 text-[#7FAF9A] px-2 md:px-3 py-1 rounded-full font-black tracking-widest whitespace-nowrap">FOUNDATION</span>}
            </div>
            <p className="text-sm md:text-base text-[#1F2933]/60 leading-relaxed mb-4 md:mb-6 font-medium">{description}</p>
            <div className="flex flex-wrap gap-2 md:gap-4 text-[10px] md:text-[11px] font-black uppercase tracking-widest">
              {oneTime > 0 && <span className="text-[#1F2933]/40 border border-[#1F2933]/10 px-2 md:px-3 py-1 rounded-lg">SGD {oneTime} Build</span>}
              {displayMonthly > 0 && <span className="text-[#7FAF9A] border border-[#7FAF9A]/20 px-2 md:px-3 py-1 rounded-lg bg-[#7FAF9A]/5">SGD {displayMonthly}/mo</span>}
            </div>
          </div>
          <div className={`w-7 h-7 md:w-8 md:h-8 rounded-full border-2 flex items-center justify-center transition-all shrink-0 mt-1 ${
            isSelected ? 'bg-[#7FAF9A] border-[#7FAF9A]' : 'border-gray-200'
          }`}>
            {isSelected && <Check size={16} className="text-white md:w-[18px] md:h-[18px]" strokeWidth={4} />}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section id="build-system" className="py-24 md:py-40 px-4 md:px-6 bg-[#F4F1EC]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 md:mb-24">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#E8C547]/10 text-[#1F2933] text-[10px] font-black uppercase tracking-[0.2em] mb-8 md:mb-10">
            <Sparkles size={14} className="text-[#E8C547]" />
            <span>Customize Your Build</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-extrabold text-[#1F2933] mb-6 md:mb-8 tracking-tight leading-[1.05]">Architect Your <br/><span className="text-[#7FAF9A]">Tiny System.</span></h2>
          <p className="text-lg md:text-xl md:text-2xl text-[#1F2933]/60 max-w-2xl mx-auto font-medium leading-relaxed">No geek-speak. No confusing servers. Just select what your business needs to run smoothly.</p>
        </div>

        {/* MOBILE STICKY SUMMARY BAR */}
        <div className="lg:hidden sticky top-20 z-40 mb-10 -mx-2">
          <div className="bg-[#1F2933] text-white rounded-[1.5rem] shadow-2xl border border-white/10 overflow-hidden transition-all duration-300">
             {/* Header - Always Visible */}
             <div 
               onClick={() => setIsMobileSummaryOpen(!isMobileSummaryOpen)}
               className="p-5 flex items-center justify-between cursor-pointer active:bg-white/5"
             >
                <div className="flex items-center gap-4">
                  <div className="bg-[#7FAF9A] p-2 rounded-xl text-[#1F2933]">
                     <Tag size={20} />
                  </div>
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 block mb-0.5">Total Estimate</span>
                    <span className="text-xl font-black tracking-tight">SGD {totals.oneTime}</span>
                  </div>
                </div>
                <div className={`p-2 rounded-full bg-white/10 transition-transform duration-300 ${isMobileSummaryOpen ? 'rotate-180' : ''}`}>
                  <ChevronDown size={20} />
                </div>
             </div>

             {/* Expandable Content */}
             <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isMobileSummaryOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-5 pb-6 border-t border-white/10 pt-6">
                  {/* Cost Breakdown */}
                  <div className="space-y-4 mb-8">
                     <div className="flex justify-between items-center text-sm">
                        <span className="text-white/60 font-bold">One-time Build</span>
                        <span className="font-black">SGD {totals.oneTime}</span>
                     </div>
                     <div className="flex justify-between items-center text-sm">
                        <span className="text-white/60 font-bold">Monthly</span>
                        <span className={`font-black ${totals.monthly > 0 ? 'text-[#7FAF9A]' : 'text-white/30'}`}>
                           {totals.monthly > 0 ? `SGD ${totals.monthly}/mo` : 'None'}
                        </span>
                     </div>
                  </div>

                  {/* Promo Input Mobile */}
                  <div className="mb-8">
                    <label className="text-[9px] font-black uppercase tracking-[0.2em] text-white/30 mb-2 block">Promo Code</label>
                    <div className="relative">
                      <input 
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        placeholder="CODE"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] outline-none focus:border-[#E8C547] transition-all placeholder:text-white/10"
                      />
                      {isDiscounted && <Tag size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7FAF9A]" />}
                    </div>
                  </div>
                  
                  {/* Items List */}
                  <div className="mb-8 p-4 rounded-2xl bg-black/20 border border-white/5">
                    <ul className="space-y-2">
                       {Object.entries(selected).filter(([_, v]) => v).map(([k]) => (
                          <li key={k} className="flex items-center gap-2 text-[11px] text-white/80 font-bold">
                            <Check size={12} className="text-[#E8C547]" />
                            {k === 'base' && 'Shopfront'}
                            {k === 'tracking' && 'Dashboard'}
                            {k === 'chatbot' && 'AI Concierge'}
                            {k === 'whatsapp' && 'WA Link'}
                            {k === 'telegram' && 'TG Link'}
                          </li>
                       ))}
                       <li className="flex items-center gap-2 text-[11px] text-[#7FAF9A] font-bold">
                          <Check size={12} />
                          {hostingRoute === 'ours' ? 'Cloud Hosting' : 'Self-Hosted'}
                       </li>
                    </ul>
                  </div>

                  <button 
                    onClick={() => setIsModalOpen(true)}
                    className="w-full bg-[#E8C547] text-[#1F2933] py-4 rounded-xl font-black text-lg hover:bg-[#f5d461] active:scale-[0.98] transition-all flex items-center justify-center gap-3"
                  >
                    Initiate Build
                    <ArrowRight size={20} />
                  </button>
                </div>
             </div>
          </div>
        </div>

        {/* Removed items-start to allow columns to stretch, enabling sticky behavior */}
        <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
          <div className="lg:col-span-2 space-y-16 md:space-y-20">
            {/* 1. The Foundation */}
            <div>
              <h3 className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.4em] text-[#1F2933]/20 mb-6 md:mb-8 flex items-center gap-4">
                <div className="w-8 md:w-12 h-[1px] bg-[#1F2933]/10"></div>
                01. The Shopfront
              </h3>
              <ItemCard 
                id="base"
                name="Instant Website App"
                description="A professional website that works like an app. Customers can open it instantly on any phone or computer—no downloads required."
                oneTime={isDiscounted ? 199 : 599}
                monthly={0}
                icon={Globe}
                required
              />
            </div>

            {/* 2. Add Superpowers */}
            <div className="space-y-6">
              <h3 className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.4em] text-[#1F2933]/20 mb-6 md:mb-8 flex items-center gap-4">
                <div className="w-8 md:w-12 h-[1px] bg-[#1F2933]/10"></div>
                02. Intelligent Superpowers
              </h3>
              <ItemCard 
                id="chatbot"
                name="24/7 AI Concierge"
                description="An AI assistant that learns your business. It replies to customer questions instantly, handles FAQs, and never sleeps."
                oneTime={249}
                monthly={20}
                icon={MessageSquare}
              />
              <ItemCard 
                id="tracking"
                name="Founders Dashboard"
                description="Your simple command center. See new leads, track orders, and manage customers from your phone without complex spreadsheets."
                oneTime={249}
                monthly={10}
                icon={Database}
              />
              <div className="grid sm:grid-cols-2 gap-6">
                <ItemCard 
                  id="whatsapp"
                  name="One-Tap WhatsApp"
                  description="A 'Chat with Us' button that opens WhatsApp instantly with a pre-typed message."
                  oneTime={69}
                  monthly={0}
                  icon={Smartphone}
                />
                <ItemCard 
                  id="telegram"
                  name="One-Tap Telegram"
                  description="A 'Chat with Us' button that opens Telegram instantly with a pre-typed message."
                  oneTime={69}
                  monthly={0}
                  icon={Send}
                />
              </div>
            </div>

            {/* 3. Logic Roots */}
            <div className="bg-white/50 p-6 md:p-14 rounded-[2.5rem] md:rounded-[3.5rem] border-2 border-[#1F2933]/5 shadow-sm">
              <h3 className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.4em] text-[#1F2933]/20 mb-8 md:mb-10 flex items-center gap-4">
                <div className="w-8 md:w-12 h-[1px] bg-[#1F2933]/10"></div>
                03. Infrastructure Roots
              </h3>
              
              <div className="space-y-8">
                {/* AI Configuration */}
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#1F2933]/40 mb-4 flex items-center gap-2">
                    <Brain size={14} />
                    How do you want to pay for AI?
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                    <button 
                      onClick={() => setAiRoute('ours')}
                      className={`p-6 md:p-8 rounded-[2rem] border-2 text-left transition-all group ${
                        aiRoute === 'ours' ? 'border-[#7FAF9A] bg-white shadow-xl shadow-green-900/5' : 'border-transparent bg-[#1F2933]/5 hover:bg-white'
                      }`}
                    >
                      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-2xl mb-4 md:mb-6 flex items-center justify-center transition-all ${aiRoute === 'ours' ? 'bg-[#7FAF9A] text-white' : 'bg-[#1F2933]/5 text-[#1F2933]/20 group-hover:text-[#7FAF9A]'}`}>
                        <Sparkles size={20} className="md:w-6 md:h-6" />
                      </div>
                      <h4 className="text-base md:text-lg font-black text-[#1F2933] mb-2">Included & Stress-Free</h4>
                      <p className="text-[11px] md:text-xs text-[#1F2933]/50 font-medium leading-relaxed">We pay the AI bills and keep it running smoothly. You don't need to worry about technical accounts.</p>
                      <div className="mt-4 inline-block text-[10px] font-black text-[#7FAF9A] bg-[#7FAF9A]/10 px-3 py-1 rounded-full">Monthly running cost</div>
                    </button>
                    <button 
                      onClick={() => setAiRoute('own')}
                      className={`p-6 md:p-8 rounded-[2rem] border-2 text-left transition-all group ${
                        aiRoute === 'own' ? 'border-[#7FAF9A] bg-white shadow-xl shadow-green-900/5' : 'border-transparent bg-[#1F2933]/5 hover:bg-white'
                      }`}
                    >
                      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-2xl mb-4 md:mb-6 flex items-center justify-center transition-all ${aiRoute === 'own' ? 'bg-[#7FAF9A] text-white' : 'bg-[#1F2933]/5 text-[#1F2933]/20 group-hover:text-[#7FAF9A]'}`}>
                        <Database size={20} className="md:w-6 md:h-6" />
                      </div>
                      <h4 className="text-base md:text-lg font-black text-[#1F2933] mb-2">I Have My Own Keys</h4>
                      <p className="text-[11px] md:text-xs text-[#1F2933]/50 font-medium leading-relaxed">For tech-savvy founders who already have an OpenAI account and want to pay them directly.</p>
                      <div className="mt-4 inline-block text-[10px] font-black text-[#1F2933]/30 bg-[#1F2933]/5 px-3 py-1 rounded-full">Free</div>
                    </button>
                  </div>
                </div>

                {/* Hosting Configuration */}
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#1F2933]/40 mb-4 flex items-center gap-2">
                    <Cloud size={14} />
                    Where should the website live?
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                    <button 
                      onClick={() => setHostingRoute('ours')}
                      className={`p-6 md:p-8 rounded-[2rem] border-2 text-left transition-all group ${
                        hostingRoute === 'ours' ? 'border-[#7FAF9A] bg-white shadow-xl shadow-green-900/5' : 'border-transparent bg-[#1F2933]/5 hover:bg-white'
                      }`}
                    >
                      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-2xl mb-4 md:mb-6 flex items-center justify-center transition-all ${hostingRoute === 'ours' ? 'bg-[#7FAF9A] text-white' : 'bg-[#1F2933]/5 text-[#1F2933]/20 group-hover:text-[#7FAF9A]'}`}>
                        <Server size={20} className="md:w-6 md:h-6" />
                      </div>
                      <h4 className="text-base md:text-lg font-black text-[#1F2933] mb-2">Hosted by Us</h4>
                      <p className="text-[11px] md:text-xs text-[#1F2933]/50 font-medium leading-relaxed">We keep your site online, secure, and fast. You don't have to touch a single server setting.</p>
                      <div className="mt-4 inline-block text-[10px] font-black text-[#7FAF9A] bg-[#7FAF9A]/10 px-3 py-1 rounded-full">S$14/mo</div>
                    </button>
                    <button 
                      onClick={() => setHostingRoute('own')}
                      className={`p-6 md:p-8 rounded-[2rem] border-2 text-left transition-all group ${
                        hostingRoute === 'own' ? 'border-[#7FAF9A] bg-white shadow-xl shadow-green-900/5' : 'border-transparent bg-[#1F2933]/5 hover:bg-white'
                      }`}
                    >
                      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-2xl mb-4 md:mb-6 flex items-center justify-center transition-all ${hostingRoute === 'own' ? 'bg-[#7FAF9A] text-white' : 'bg-[#1F2933]/5 text-[#1F2933]/20 group-hover:text-[#7FAF9A]'}`}>
                        <Globe size={20} className="md:w-6 md:h-6" />
                      </div>
                      <h4 className="text-base md:text-lg font-black text-[#1F2933] mb-2">I'll Host It Myself</h4>
                      <p className="text-[11px] md:text-xs text-[#1F2933]/50 font-medium leading-relaxed">We send you the code. You handle the servers, security, and technical setup.</p>
                      <div className="mt-4 inline-block text-[10px] font-black text-[#1F2933]/30 bg-[#1F2933]/5 px-3 py-1 rounded-full">Free</div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Visual Identity */}
            <div className="space-y-12">
              <h3 className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.4em] text-[#1F2933]/20 mb-6 md:mb-8 flex items-center gap-4">
                <div className="w-8 md:w-12 h-[1px] bg-[#1F2933]/10"></div>
                04. Visual Identity
              </h3>
              
              <div className="bg-white p-6 md:p-12 rounded-[2.5rem] border border-[#1F2933]/5 shadow-sm">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#1F2933]/30 mb-6 flex items-center gap-2">
                   <Palette size={14} /> Design & Polish
                </h4>
                <div className="grid sm:grid-cols-2 gap-6">
                  <button 
                    onClick={() => setBrandRoute('own')}
                    className={`w-full p-5 md:p-6 rounded-2xl border-2 text-left transition-all ${brandRoute === 'own' ? 'border-[#7FAF9A] bg-green-50/30' : 'border-transparent bg-gray-50'}`}
                  >
                    <span className="block font-black text-base md:text-lg text-[#1F2933] mb-1">Follow my Brand Kit</span>
                    <span className="block text-[11px] md:text-xs text-[#1F2933]/60 mb-3">You provide your logo and colors, we simply apply them to the system.</span>
                    <span className="text-[10px] font-black text-gray-400 bg-white px-2 py-1 rounded border border-gray-100">FREE</span>
                  </button>
                  <button 
                    onClick={() => setBrandRoute('pro')}
                    className={`w-full p-5 md:p-6 rounded-2xl border-2 text-left transition-all ${brandRoute === 'pro' ? 'border-[#7FAF9A] bg-green-50/30' : 'border-transparent bg-gray-50'}`}
                  >
                    <span className="block font-black text-base md:text-lg text-[#1F2933] mb-1">Design It For Me</span>
                    <span className="block text-[11px] md:text-xs text-[#1F2933]/60 mb-3">We work with you to create a fresh, professional look for your brand.</span>
                    <span className="text-[10px] font-black text-[#7FAF9A] bg-white px-2 py-1 rounded border border-[#7FAF9A]/20">S$99</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* System Summary Sticky Card - Wrapped in a container to track full height */}
          <div className="hidden lg:block relative h-full">
            <div className="sticky top-32 bg-[#1F2933] text-white p-12 rounded-[3.5rem] shadow-2xl overflow-hidden relative border border-white/5">
              <div className="absolute inset-0 opacity-5 pointer-events-none">
                <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
              </div>

              <div className="relative z-10">
                <h3 className="text-3xl font-black mb-10 tracking-tight">Your Quote</h3>
                
                <div className="space-y-6 mb-10 pb-10 border-b border-white/10">
                  <div className="flex justify-between items-end">
                    <div>
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 block mb-1">One-time Build</span>
                      <span className="text-2xl font-black">SGD {totals.oneTime}</span>
                    </div>
                    {isDiscounted && <span className="text-[10px] line-through text-white/20 mb-1">SGD {totals.oneTime + 400}</span>}
                  </div>
                  <div className="flex justify-between items-end">
                    <div>
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 block mb-1">Monthly Management</span>
                      <span className={`text-2xl font-black ${totals.monthly > 0 ? 'text-[#7FAF9A]' : 'text-white/20'}`}>
                        {totals.monthly > 0 ? `SGD ${totals.monthly}/mo` : 'None'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Promo Code Input */}
                <div className="mb-10">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-white/30 mb-3 block">Apply Discount</label>
                  <div className="relative">
                    <input 
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      placeholder="ENTER CODE"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-xs font-bold uppercase tracking-[0.2em] outline-none focus:border-[#E8C547] transition-all placeholder:text-white/10"
                    />
                    {isDiscounted && <Tag size={18} className="absolute right-5 top-1/2 -translate-y-1/2 text-[#7FAF9A]" />}
                  </div>
                  {isDiscounted && <p className="text-[10px] text-[#7FAF9A] font-black mt-3 uppercase tracking-widest animate-pulse">Founders Friend Discount Active</p>}
                </div>

                <div className="mb-12">
                  <div className="text-[10px] font-black uppercase tracking-[0.2em] text-white/30 mb-6 flex items-center gap-2">
                    <Info size={12} />
                    Build Scope
                  </div>
                  <ul className="space-y-4">
                    {Object.entries(selected).filter(([_, v]) => v).map(([k]) => (
                      <li key={k} className="flex items-center gap-3 text-xs text-white font-bold">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#E8C547] shadow-[0_0_8px_rgba(232,197,71,0.5)]"></div>
                        {k === 'base' && 'Shopfront Core'}
                        {k === 'tracking' && 'Founder Dashboard'}
                        {k === 'chatbot' && 'AI Concierge'}
                        {k === 'whatsapp' && 'WhatsApp Link'}
                        {k === 'telegram' && 'Telegram One-Tap'}
                      </li>
                    ))}
                    <li className="flex items-center gap-3 text-xs text-[#7FAF9A] font-bold">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#7FAF9A]"></div>
                      {hostingRoute === 'ours' ? 'Tiny Cloud Hosting' : 'Self-Hosted Setup'}
                    </li>
                    {aiRoute === 'own' && (
                      <li className="flex items-center gap-3 text-xs text-[#E8C547] font-bold">
                        <Brain size={14} className="opacity-70" />
                        Using Your Own AI Keys
                      </li>
                    )}
                  </ul>
                </div>

                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="w-full bg-[#E8C547] text-[#1F2933] py-6 rounded-[1.5rem] font-black text-xl hover:bg-[#f5d461] hover:scale-[1.02] active:scale-[0.98] transition-all shadow-2xl shadow-amber-900/20 flex items-center justify-center gap-4"
                >
                  Initiate Build
                  <ArrowRight size={24} />
                </button>
                
                <p className="mt-8 text-[9px] text-center text-white/20 font-black uppercase tracking-[0.4em] flex items-center justify-center gap-3">
                  <Shield size={12} />
                  Frictionless Delivery Guaranteed
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lead Capture Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6 bg-[#1F2933]/90 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] md:rounded-[3.5rem] w-full max-w-xl p-8 md:p-16 relative shadow-2xl animate-in zoom-in-95 duration-200 overflow-y-auto max-h-full">
            <button 
              onClick={closeModal}
              className="absolute top-6 right-6 md:top-10 md:right-10 p-3 text-gray-300 hover:text-gray-900 transition-colors bg-gray-50 rounded-full"
            >
              <X size={24} />
            </button>
            
            {isSuccess ? (
              <div className="text-center py-6">
                <div className="w-20 h-20 md:w-24 md:h-24 bg-[#7FAF9A]/10 text-[#7FAF9A] rounded-full flex items-center justify-center mx-auto mb-10 animate-in zoom-in-50 duration-500">
                  <Check size={48} className="md:w-[56px] md:h-[56px]" strokeWidth={3} />
                </div>
                <h3 className="text-3xl md:text-4xl font-black text-[#1F2933] mb-6 tracking-tight">Thank you!</h3>
                <p className="text-[#1F2933]/60 font-bold text-base md:text-lg mb-12 leading-relaxed">
                  We will be in touch within <span className="text-[#1F2933]">3 working days</span> to confirm your Tiny System.
                </p>
                <button 
                  onClick={closeModal}
                  className="mt-14 text-[#1F2933]/30 text-[10px] font-black uppercase tracking-[0.4em] hover:text-[#1F2933] transition-colors"
                >
                  Return to Studio
                </button>
              </div>
            ) : (
              <>
                <div className="mb-8 md:mb-10 pt-4">
                  <h3 className="text-3xl md:text-4xl font-black text-[#1F2933] mb-4 tracking-tight">Final Details.</h3>
                  <p className="text-[#1F2933]/50 font-bold text-base md:text-lg leading-relaxed">Tell us about your craft so we can configure your system correctly.</p>
                </div>

                <form onSubmit={handleCheckout} className="space-y-6 md:space-y-8">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#1F2933]/30">The Founder</label>
                    <input 
                      required
                      type="text" 
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-6 md:px-8 py-4 md:py-5 rounded-2xl border-2 border-[#1F2933]/5 focus:border-[#7FAF9A] outline-none transition-all font-bold text-lg bg-[#F4F1EC]/30"
                      placeholder="e.g. Jane Doe"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#1F2933]/30">The Business / Craft</label>
                    <input 
                      required
                      type="text" 
                      value={formData.business}
                      onChange={(e) => setFormData({...formData, business: e.target.value})}
                      className="w-full px-6 md:px-8 py-4 md:py-5 rounded-2xl border-2 border-[#1F2933]/5 focus:border-[#7FAF9A] outline-none transition-all font-bold text-lg bg-[#F4F1EC]/30"
                      placeholder="e.g. Artisanal Candle Maker"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#1F2933]/30">Primary Email</label>
                    <input 
                      required
                      type="email" 
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-6 md:px-8 py-4 md:py-5 rounded-2xl border-2 border-[#1F2933]/5 focus:border-[#7FAF9A] outline-none transition-all font-bold text-lg bg-[#F4F1EC]/30"
                      placeholder="jane@studio.com"
                    />
                  </div>

                  <div className="pt-6">
                    <button 
                      disabled={isSubmitting}
                      className="w-full bg-[#7FAF9A] text-white py-5 md:py-6 rounded-2xl font-black text-lg md:text-xl hover:bg-[#6b9a87] hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-4 disabled:opacity-50 shadow-xl shadow-green-900/10"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={24} className="animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          Confirm Specs
                          <ArrowRight size={24} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default Pricing;
